import React, { useState } from "react";
import { Flex, Select, Table, Text } from "@telia/teddy";
import { HSSalesPackage } from "../../../../../../models/server/homeSales";

interface FwaPackagesTableProps {
    packages: HSSalesPackage[];
    selectedPackageId: number | null;
    onSelectPackage: (id: number | null) => void;
}

const FwaPackagesTable: React.FC<FwaPackagesTableProps> = ({ packages, selectedPackageId, onSelectPackage }) => {
    const [filterInstallation, setFilterInstallation] = useState<"Professional" | "Selfinstall" | null>("Selfinstall");
    const [filterTv, setFilterTv] = useState<"tv_boks" | "streaming" | "no_tv" | null>("no_tv");

    const filteredPackages = packages.filter((pkg) => {
        if (
            filterInstallation &&
            !(pkg.configuration.installation ?? "").toLowerCase().includes(filterInstallation.toLowerCase())
        )
            return false;
        if (filterTv === "tv_boks" && !pkg.configuration.tv.includes("Telia TV boks")) return false;
        if (filterTv === "streaming" && !pkg.configuration.tv.includes("Telia Play Strømming")) return false;
        if (filterTv === "no_tv" && !pkg.configuration.tv.includes("Ingen TV")) return false;
        return true;
    });

    return (
        <>
            <Flex direction="row" gap="300" mb="300">
                <Select
                    label="Installasjon"
                    name="filter-installation"
                    options={["Selfinstall", "Professional"]}
                    value={filterInstallation ?? ""}
                    onValueChange={(value: string) => {
                        setFilterInstallation(
                            value === filterInstallation ? null : (value as "Selfinstall" | "Professional"),
                        );
                        return value;
                    }}
                >
                    <Select.Trigger>
                        <Select.Value placeholder="Alle" />
                    </Select.Trigger>
                    <Select.Portal>
                        <Select.Content>
                            <Select.Viewport>
                                <Select.Item value="Selfinstall">Montere selv</Select.Item>
                                <Select.Item value="Professional">Bestill montør</Select.Item>
                            </Select.Viewport>
                        </Select.Content>
                    </Select.Portal>
                </Select>
                <Select
                    label="TV"
                    name="filter-tv"
                    options={["no_tv", "tv_boks", "streaming"]}
                    value={filterTv ?? ""}
                    onValueChange={(value: string) => {
                        setFilterTv(value === filterTv ? null : (value as "tv_boks" | "streaming" | "no_tv"));
                        return value;
                    }}
                >
                    <Select.Trigger>
                        <Select.Value placeholder="Alle" />
                    </Select.Trigger>
                    <Select.Portal>
                        <Select.Content>
                            <Select.Viewport>
                                <Select.Item value="no_tv">Uten TV</Select.Item>
                                <Select.Item value="tv_boks">TV &amp; Strømming m/TV-boks</Select.Item>
                                <Select.Item value="streaming">TV &amp; Strømming u/TV-boks</Select.Item>
                            </Select.Viewport>
                        </Select.Content>
                    </Select.Portal>
                </Select>
            </Flex>
            <Table>
                <Table.Header>
                    <Table.Row>
                        <Table.Head />
                        <Table.Head>Hastighet</Table.Head>
                        <Table.Head>Månedspris</Table.Head>
                        <Table.Head>Installasjon</Table.Head>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {filteredPackages.map((pkg) => {
                        const showStrikethrough =
                            pkg.monthlyPrice !== null &&
                            pkg.monthlyPrice.discountPrice !== pkg.monthlyPrice.regularPrice;
                        return (
                            <Table.Row
                                key={pkg.id}
                                style={{ cursor: "pointer" }}
                                onClick={() => onSelectPackage(selectedPackageId === pkg.id ? null : pkg.id)}
                            >
                                <Table.Cell style={{ width: "1px", paddingRight: "0.25rem" }}>
                                    <input
                                        type="radio"
                                        name="fwa-package"
                                        checked={selectedPackageId === pkg.id}
                                        aria-label={`Velg pakke: ${pkg.configuration.speed || "FWA pakke"}`}
                                        onChange={() => onSelectPackage(selectedPackageId === pkg.id ? null : pkg.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            cursor: "pointer",
                                            // TODO(dark-mode): review, native radio accent color, suggested theme interactive token
                                            accentColor: "#6c0baf",
                                            display: "block",
                                        }}
                                    />
                                </Table.Cell>
                                <Table.Cell>{pkg.configuration.speed || "–"}</Table.Cell>
                                <Table.Cell>
                                    {pkg.monthlyPrice ? (
                                        <>
                                            {showStrikethrough && (
                                                <span
                                                    style={{
                                                        marginRight: "0.5rem",
                                                        textDecoration: "line-through",
                                                    }}
                                                >
                                                    <Text as="span" variant="paragraph-100">
                                                        {Math.round(Number(pkg.monthlyPrice.regularPrice))},-
                                                    </Text>
                                                </span>
                                            )}
                                            <Text as="span" variant="paragraph-100">
                                                {Math.round(Number(pkg.monthlyPrice.discountPrice))},- /md.
                                            </Text>
                                        </>
                                    ) : (
                                        "–"
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    {pkg.installationPrice
                                        ? `${Math.round(Number(pkg.installationPrice.discountPrice))},-`
                                        : pkg.configuration.installation || "–"}
                                </Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>
        </>
    );
};

export default FwaPackagesTable;
